import React from 'react';
import { Shield, Swords, Star } from 'lucide-react';
import { BattleHealthBar } from './BattleHealthBar';
import { BattlePet } from './BattlePet';
import { BattleLog } from './BattleLog';
import type { UserPet } from '../../types/pets';
import type { UserItem } from '../../types/items';
import type { BattleEffect } from '../../types/battle';

interface BattleFieldProps {
  playerPet: UserPet;
  aiPet: UserPet;
  playerHealth: number;
  aiHealth: number;
  playerItems: UserItem[];
  aiItems: UserItem[];
  isPlayerTurn: boolean;
  lastEffect?: BattleEffect;
}

export function BattleField({
  playerPet,
  aiPet,
  playerHealth,
  aiHealth,
  playerItems,
  aiItems,
  isPlayerTurn,
  lastEffect
}: BattleFieldProps) {
  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-900/50 to-gray-900/50 backdrop-blur-sm rounded-xl" />

      <div className="relative grid grid-cols-2 gap-8 p-8">
        {/* Player's Side */}
        <div className="space-y-4">
          <BattleHealthBar
            current={playerHealth}
            max={100}
            label="Your Health"
            color="cyan"
          />
          <BattlePet
            pet={playerPet}
            items={playerItems}
            position="bottom"
            isActive={isPlayerTurn}
          />
        </div>

        {/* AI's Side */}
        <div className="space-y-4">
          <BattleHealthBar
            current={aiHealth}
            max={100}
            label="Enemy Health"
            color="pink"
          />
          <BattlePet
            pet={aiPet}
            items={aiItems}
            position="top"
            isActive={!isPlayerTurn}
          />
        </div>

        {/* Battle Log */}
        {lastEffect && (
          <BattleLog effect={lastEffect} />
        )}

        {/* Turn Indicator - Centered */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-6 py-3 bg-gray-800/90 rounded-full shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-2 text-lg font-medium">
            {isPlayerTurn ? (
              <>
                <Shield className="w-5 h-5 text-cyan-400" />
                <span className="text-cyan-400">Your Turn</span>
              </>
            ) : (
              <>
                <Swords className="w-5 h-5 text-pink-400" />
                <span className="text-pink-400">Enemy Turn</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}